import { MathUtils } from "three";
import { ViewType } from "../Models/AppModel.js";
import { AnatomicalSideEnum, BoneTypeEnum, ReferenceTypeEnum } from "../Models/CaseModel.js";
import { AnatomicalAxisType } from "../Tools/AnatomicalAxis/AnatomicalAxis.js";
import { Consts, ConvexityValue } from "./Consts.js";
export const result = [];
export function getPositionFromObject3D(object) {
    return object.position ?? object;
}
export function getPositionsFromObject3D(objects) {
    for (let i = 0, l = objects.length; i < l; i++) {
        result[i] = getPositionFromObject3D(objects[i]);
    }
    return result;
}
export function setBit(mask, index) {
    return mask | (1 << index);
}
export function unsetBit(mask, index) {
    return mask & ~(1 << index);
}
export function isBitSet(mask, index) {
    return (mask & (1 << index)) !== 0;
}
export function getFirstUnsetBit(mask, maxBits = 32) {
    for (let i = 0; i < maxBits; i++) {
        if (!isBitSet(mask, i)) {
            return i;
        }
    }
    return -1;
}
export function areAllBitsSet(mask, bitsCount) {
    const fullMask = (1 << bitsCount) - 1;
    return mask === fullMask;
}
export function hasAtLeastOneBitSet(mask) {
    return mask !== 0;
}
/**
 * Contains some useful functions.
 */
// @dynamic
export class Utils {
    /**
     * Swaps two elements in an array.
     */
    static swapItemInArray(index1, index2, array) {
        const temp = array[index1];
        array[index1] = array[index2];
        array[index2] = temp;
    }
    /**
     * Checks if an angle in radiants is in a range expressed in degrees.
     */
    static isRadiantsAngleInDegreesRange(value, min, max) {
        const rad = Math.round(MathUtils.radToDeg(value));
        return rad >= min && rad <= max;
    }
    /**
    * Checks if a value is in a range.
    */
    static isInRange(value, min, max) {
        return value >= min && value <= max;
    }
    /**
     * Checks if an angle in dregrees is in a specific Paley angle range.
     */
    static isPaleyAngleValid(value, range) {
        return Utils.isInRange(value, range.min, range.max);
    }
    /**
     * Converts a value in float and rounds it.
     */
    static numberRound(value, digits) {
        return Number.parseFloat(value.toFixed(digits));
    }
    /**
     * Convert a Vector2D arary in a number array with 3 components (putting 0 on z).
     * Example: [Vec2(1,2), Vec(3,4)] -> [1,2,0,3,4,0]
     */
    static parseVec2ArrayToNumber3Array(array) {
        const ret = [];
        for (const item of array) {
            ret.push(item.x, item.y, 0);
        }
        return ret;
    }
    /**
     * Check if the bone is a foot.
     */
    static isFoot(bone) {
        return bone == BoneTypeEnum.Forefoot || bone == BoneTypeEnum.Hindfoot;
    }
    /**
     * Get the convexity data for the specific case configuration.
     */
    static getConvexity(viewType, bone, side, referenceType) {
        const ret = {
            Reference: { DefaultConvexity: ConvexityValue.Conv_1_2, AvailableConvexity: [ConvexityValue.Conv_1_2] },
            Moving: { DefaultConvexity: ConvexityValue.Conv_1_2, AvailableConvexity: [ConvexityValue.Conv_1_2] }
        };
        const isLeft = side === AnatomicalSideEnum.Left;
        const isProximal = referenceType === ReferenceTypeEnum.Proximal;
        if (viewType === ViewType.LT && !this.isFoot(bone)) {
            const convexStruct = Consts.convexityList[bone];
            if ((isProximal && convexStruct.TopEffected) || (!isProximal && convexStruct.BottomEffected)) {
                ret.Reference.DefaultConvexity = isLeft ? 1 - convexStruct.DefaultValue : convexStruct.DefaultValue;
                ret.Reference.AvailableConvexity = [];
                for (const value of convexStruct.ConvexList) {
                    ret.Reference.AvailableConvexity.push(isLeft ? 1 - value : value);
                }
            }
            if ((isProximal && convexStruct.BottomEffected) || (!isProximal && convexStruct.TopEffected)) {
                ret.Moving.DefaultConvexity = isLeft ? 1 - convexStruct.DefaultValue : convexStruct.DefaultValue;
                ret.Moving.AvailableConvexity = [];
                for (const value of convexStruct.ConvexList) {
                    ret.Moving.AvailableConvexity.push(isLeft ? 1 - value : value);
                }
            }
        }
        return ret;
    }
    /**
     * Get anatomical axis A1A2 lengths.
     */
    static getAnatomicalAxisLengths(viewType, bone, axisType, refType) {
        const type = refType === ReferenceTypeEnum.Proximal ?
            axisType === AnatomicalAxisType.reference ? "ref" : "mov" :
            axisType === AnatomicalAxisType.reference ? "mov" : "ref";
        return Consts.anatomicalAxisLengths[bone][ViewType[viewType]][type]; // TODO improve this removing enum
    }
    /**
     * Get recursively all draggable points from a Object3d.
     */
    static getAllDraggablePoints(obj, target = []) {
        for (const child of obj.children) {
            if (child.isDraggablePoint) {
                target.push(child);
            }
            else {
                this.getAllDraggablePoints(child, target);
            }
        }
        return target;
    }
    static getAllObjects(obj, filterType, excludedObjs = [], excludedObjsName = [], target = []) {
        for (const child of obj.children) {
            if (excludedObjs.filter(x => x === child).length == 0 && excludedObjsName.filter(x => x === child.name).length == 0) {
                if (child instanceof filterType) {
                    target.push(child);
                }
                else {
                    this.getAllObjects(child, filterType, excludedObjs, excludedObjsName, target);
                }
            }
        }
        return target;
    }
    /**
     * Get excess value in a specular interval (positive and negative rangeValue).
     * Example: value: 91, rangeValue 90 => 1 // value: -95, rangeValue 90 => -5 // value: 50, rangeValue 90 => 0
     */
    static getExcessInSpecularRange(value, rangeValue) {
        return Math.max(Math.abs(value) - Math.abs(rangeValue), 0) * Math.sign(value);
    }
    /**
     * Get max excess value in a specular interval (positive and negative rangeValue).
     * Example: value: -91, -92, rangeValue 90 => -2 // value: -95, 96, rangeValue 90 => 6 // value: 50, -50, rangeValue 90 => 0
     */
    static getMaxExcessInSpecularRange(rangeValue, ...values) {
        let max = 0;
        let sign = 0;
        rangeValue = Math.abs(rangeValue);
        for (const value of values) {
            const temp = Math.abs(value) - rangeValue;
            if (temp > max) {
                max = temp;
                sign = Math.sign(value);
            }
        }
        return max * sign;
    }
    /**
     * Fix an angle in radiants in an interval between -Math.PI / 2 and Math.PI / 2.
     * Example (in degrees to semplify): -91 -> 89 // 170 -> -10 // 50 -> 50
     */
    static fixAngleIn90Range(value) {
        return Math.abs(value) <= Math.PI / 2 ? value : (Math.abs(value) - Math.PI) * Math.sign(value);
    }
    /**
     * Clone an object.
     */
    static clone(obj) {
        return JSON.parse(JSON.stringify(obj)); // TODO use structuredClone
    }
    /**
     * Get scene from a Object3D.
     */
    static getScene(obj) {
        while (obj = obj.parent) {
            if (obj?.isScene)
                return obj;
        }
        return null;
    }
}
//# sourceMappingURL=Utils.js.map